import PropTypes from 'prop-types';
import Decorator from 'clientDecorator';
import EverTrue from 'app';
import { Button } from '@evertrue/et-components';
import { isEmpty } from '@evertrue/sdk';
import InteractionSnippetCard from 'apps/interactions/components/interaction-snippet-card';
import InteractionUtils from 'apps/interactions/interaction-utils';
import StringSearchDisplay from 'components/formatting/string-search-display';
import AISearchCard from 'apps/ai-search/components/ai-search-card';
import GlobalSearchDropdownHeader from './global-search-dropdown-header';
import GlobalSearchContactCard from './global-search-contact-card';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';
import WindowStore from 'apps/layout/stores/window-store';
import { useFluxStore } from '@evertrue/et-flux';
import { useGlobalSearch } from 'apps/layout/global-search/global-search-context';
import { useGate } from 'components/is-gated';

const mapWindowState = () => ({
  windowHeight: WindowStore.getHeight(),
});

const GlobalSearchDropdown = ({ linkButtonRef, handleGlobalSearch = () => {}, handleOutsideClick = () => {} }) => {
  const { windowHeight } = useFluxStore(WindowStore, mapWindowState);
  const { flagEnabled: hasAI } = useFeatureFlag('aiGeneral');
  const { flagEnabled: hasNLS } = useFeatureFlag('nlsEarlyAdopter');
  const hasInteractions = useGate('ugc_show_imported_notes');

  const { contactResults, interactionResults, loading, query, queryType } = useGlobalSearch();

  const styles = {
    marginBottom: !hasInteractions || (isEmpty(contactResults) && isEmpty(interactionResults)) ? 0 : null,
    maxHeight: windowHeight - 146, // account for header and padding and footer
  };

  const handleContactClick = (contactId) => {
    handleOutsideClick();
    EverTrue.Navigator(`/contact/${contactId}`, { trigger: true });
  };

  const handleInteractionClick = (interactionId) => {
    handleOutsideClick();
    EverTrue.Navigator(`/interaction/${interactionId}`, { trigger: true });
  };

  const hasNoResults = isEmpty(contactResults) && isEmpty(interactionResults);
  const totalResults = contactResults.length + interactionResults.length;
  const resultsMessage = hasNoResults
    ? 'No search results found.'
    : `Showing ${totalResults} search result${totalResults === 1 ? '' : 's'}.`;

  const renderViewAllButton = () => {
    if (!loading && (!isEmpty(contactResults) || !isEmpty(interactionResults))) {
      return (
        <Button
          type="simple"
          ref={linkButtonRef}
          title="View all results"
          className={'global-search--footer'}
          onClick={handleGlobalSearch}
        >
          View all results for {<StringSearchDisplay string={query} />}
        </Button>
      );
    }
  };

  return (
    <div className="global-search-dropdown">
      <div role="status" aria-live="polite" aria-atomic="true" aria-relevant="additions removals text" hidden>
        {loading ? 'Loading search results...' : resultsMessage}
      </div>
      <ul className="global-search--list" styles={styles}>
        {(isEmpty(contactResults) && isEmpty(interactionResults)) ||
          (loading && <li className="global-search--empty">{loading ? 'Loading...' : 'No results found'}</li>)}

        {!loading && hasNLS && hasAI && (
          <li className="global-autocomplete--item">
            <AISearchCard
              query={query}
              handleOutsideClick={handleOutsideClick}
              aria-label="AI search suggestion"
              tabIndex={0}
            />
          </li>
        )}

        {!loading && !isEmpty(contactResults) && (
          <>
            <li className="global-search--group-header">
              <GlobalSearchDropdownHeader
                label="Contacts"
                total={contactResults.length}
                handleGlobalSearch={handleGlobalSearch}
              />
            </li>
            {contactResults.map((result, index) => {
              const contact = Decorator.Contacts.parse(result);
              return (
                <li key={contact.id} onClick={() => handleContactClick(contact.id)}>
                  <GlobalSearchContactCard contact={contact} query={query} queryType={queryType} />
                </li>
              );
            })}
          </>
        )}

        {hasInteractions && !loading && !isEmpty(interactionResults) && (
          <>
            <li className="global-search--group-header">
              <GlobalSearchDropdownHeader
                label="Interactions"
                total={interactionResults.length}
                handleGlobalSearch={handleGlobalSearch}
              />
            </li>
            {interactionResults.map((result, index) => {
              return (
                <li
                  key={result.id}
                  className={`global-autocomplete--item ${result?.pinned ? 'interaction-list-item--pinned' : ''}`}
                  onClick={() => handleInteractionClick(result.id)}
                >
                  <InteractionSnippetCard
                    interaction={result}
                    highlight={InteractionUtils.getHighlightRegex(query)}
                    contactId={result.target_id}
                  />
                </li>
              );
            })}
          </>
        )}
      </ul>
      {renderViewAllButton()}
    </div>
  );
};

GlobalSearchDropdown.propTypes = {
  linkButtonRef: PropTypes.any,
  handleGlobalSearch: PropTypes.func,
  handleOutsideClick: PropTypes.func,
};

export default GlobalSearchDropdown;
